import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import Consultation from "@/components/LeadForm";
import Footer from "@/components/Footer";
import { MountFade as FadeUp } from "@/components/motion";
import Seo, { SITE_URL } from "@/lib/seo";
import { fetchArticles, staticArticles } from "@/lib/articles";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });

export function Block({ block }) {
  if (block.type === "h2")
    return <h2 className="font-display text-[26px] md:text-[30px] font-bold text-[#F8F8F5] mt-12 mb-4 leading-snug">{block.content}</h2>;
  if (block.type === "ul")
    return (
      <ul className="my-6 space-y-4 pl-6 list-disc marker:text-[#DFFF00]">
        {block.content.map((li, i) => (
          <li key={i} className="font-body text-[18px] md:text-[20px] text-[#E8E8E6] leading-[1.6] break-words">{li}</li>
        ))}
      </ul>
    );
  if (block.type === "links")
    return (
      <ul className="my-6 space-y-4 pl-6 list-disc marker:text-[#DFFF00]">
        {block.content.map((l, i) => (
          <li key={i} className="font-body text-[18px] md:text-[20px] leading-[1.6] break-words">
            <a
              href={l.href}
              target={l.href.startsWith("/") ? undefined : "_blank"}
              rel={l.href.startsWith("/") ? undefined : "noopener noreferrer"}
              data-testid={`article-source-link-${i}`}
              className="text-[#DFFF00] hover:underline underline-offset-4"
            >
              {l.text}
            </a>
          </li>
        ))}
      </ul>
    );
  return <p className="my-6 font-body text-[18px] md:text-[20px] text-[#E8E8E6] leading-[1.6] break-words">{block.content}</p>;
}

export default function Article() {
  const { slug } = useParams();
  const [list, setList] = useState(null); // null until the API resolves; static is the instant fallback

  useEffect(() => window.scrollTo(0, 0), [slug]);
  useEffect(() => {
    let on = true;
    fetchArticles().then((l) => on && setList(l));
    return () => { on = false; };
  }, []);

  const source = list || staticArticles();
  const article = source.find((a) => a.slug === slug);

  if (!article && !list) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#DFFF00]" size={28} />
      </div>
    );
  }
  if (!article) return <Navigate to="/insights" replace />;

  const idx = source.findIndex((a) => a.slug === slug);
  const next = source[(idx + 1) % source.length];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.seoDescription,
    datePublished: article.date,
    author: { "@type": "Person", name: "George Barrett" },
    publisher: { "@type": "Organization", name: "Barrett Career Brand" },
    mainEntityOfPage: `${SITE_URL}/insights/${article.slug}`,
  };

  return (
    <main data-testid={`article-${article.slug}`} className="bg-[#0A0A0A] text-[#F8F8F5]">
      <Seo title={article.seoTitle || `${article.title} — Barrett Career Brand`} description={article.seoDescription} path={`/insights/${article.slug}`} jsonLd={jsonLd} />

      <header className="border-b border-[#27272A]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tighter">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
          <Link to="/insights" className="font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors flex items-center gap-1">
            <ArrowLeft size={15} /> All insights
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20 md:pt-24">
        <FadeUp>
          <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#DFFF00]">{article.category}</p>
          <h1 className="mt-4 font-display font-bold tracking-tighter leading-[1.05] text-4xl md:text-5xl">{article.title}</h1>
          <p className="mt-5 font-body text-sm text-[#52525B]">By George Barrett · {fmtDate(article.date)} · {article.readingTime}</p>
          {article.intro && (
            <p data-testid="article-intro" className="mt-7 font-body text-[18px] md:text-[20px] text-[#F8F8F5] leading-[1.6] border-l-2 border-[#DFFF00] pl-5 break-words">
              {article.intro}
            </p>
          )}
        </FadeUp>
        <div className="mt-8">
          {article.body.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>

        <div className="mt-14 border border-[#27272A] p-8 bg-[#141414]">
          {article.cta ? (
            <>
              <h3 className="font-display text-2xl font-bold text-[#F8F8F5]">{article.cta.text}</h3>
              {article.cta.description && (
                <p className="mt-3 font-body text-[#A1A1AA] leading-relaxed">{article.cta.description}</p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                <a
                  href={article.cta.href}
                  data-testid="article-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 hover:bg-[#C8E600] transition-colors"
                >
                  {article.cta.label} <ArrowRight size={18} />
                </a>
                {article.cta.secondaryHref && (
                  <a
                    href={article.cta.secondaryHref}
                    data-testid="article-cta-secondary"
                    className="font-body font-semibold text-[#DFFF00] hover:underline underline-offset-4"
                  >
                    {article.cta.secondaryLabel}
                  </a>
                )}
              </div>
            </>
          ) : (
            <>
              <h3 className="font-display text-2xl font-bold text-[#F8F8F5]">Want this done for you?</h3>
              <p className="mt-2 font-body text-[#A1A1AA] leading-relaxed">
                Get a free CV Career Brand Score, or request a free 15-minute consultation with George.
              </p>
              <a
                href="/#cv-review"
                data-testid="article-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 hover:bg-[#C8E600] transition-colors"
              >
                Get your free score <ArrowRight size={18} />
              </a>
            </>
          )}
        </div>

        <Link
          to={`/insights/${next.slug}`}
          data-testid="article-next"
          className={`mt-12 block border-t border-[#27272A] pt-8 group ${next.slug === article.slug ? "hidden" : ""}`}
        >
          <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">Read next</p>
          <p className="mt-2 font-display text-xl font-bold text-[#F8F8F5] group-hover:text-[#DFFF00] transition-colors">{next.title}</p>
        </Link>
      </article>

      <Consultation />
      <Footer />
    </main>
  );
}
